import { FieldPath, SubmitHandler } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import * as Avatar from '@givebutter/primitives/avatar';
import { Button } from '@givebutter/primitives/button';
import { Label } from '@givebutter/primitives/label';
import * as RadioGroup from '@givebutter/primitives/radio-group';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { faChartMixed } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createReport, fetchReportTemplates } from 'api/reports';
import * as Dialog from 'components/dialog';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useReportSchemas } from 'customHooks/useReportSchemas';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { first } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Report, ReportResource } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

type ReportCreationType = 'template' | 'resource';

interface CreateReportValues {
    fromTemplate: ReportCreationType;
    name: string;
    template: string;
    resource: ReportResource;
}

const CreateReportDialog = () => {
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const history = useHistory();
    const { error, success } = useToast();
    const { data: schemas } = useReportSchemas();
    const {
        campaignReporting = false,
        ticketReporting = false,
        recurringPlanReporting = false,
        pledgeReporting = false,
    } = useFlags();

    const { data } = useSuspenseQuery({
        queryKey: accountQueryKeys.reportTemplates(accountId),
        queryFn: () => fetchReportTemplates(),
    });
    const templates = data.data;
    const defaultTemplate = first(templates);

    const methods = Form.useGivebutterForm<CreateReportValues>({
        defaultValues: {
            name: '',
            fromTemplate: 'template',
            resource: 'contacts',
            template: defaultTemplate?.id.toString() ?? '',
        },
    });
    const { watch, setError, control, path, handleSubmit, resetFormButton } = methods;
    const fromTemplate = watch('fromTemplate');
    const isTemplate = fromTemplate === 'template';

    const { isPending, mutate: submit } = useMutation({
        mutationFn: (values: CreateReportValues) => {
            const templateId = values.template;
            const template = templates.find(t => t.id === parseInt(templateId));
            const isTemplate = values.fromTemplate === 'template';
            const resource = isTemplate && template ? template.resource : values.resource;
            const query =
                isTemplate && template
                    ? {
                          filters: template.query.filters,
                          display_settings: template.query.display_settings,
                          columns: template.query.columns,
                          graph_settings: template.query.graph_settings,
                          date_range: template.query.date_range,
                      }
                    : (schemas.find(s => s.resource === resource)?.default_query ?? null);

            return createReport(accountId, {
                name: values.name,
                resource,
                query,
                template_id: isTemplate && template ? template.id : null,
                description: isTemplate && template ? template.description : null,
            });
        },
        onSuccess: report => {
            history.push(`/accounts/${accountId}/reports/${report.id}`);
            success('Successfully created report!');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.reports(accountId),
            });
        },
        onError: (e: GivebutterAxiosError) => {
            const errors = e.response?.data.errors;
            if (errors) {
                Object.keys(errors).forEach(key => {
                    const normalizedKey = transformServerKeyIntoFormKey(
                        key as Parameters<typeof transformServerKeyIntoFormKey>[0],
                    );

                    if (normalizedKey) {
                        if (normalizedKey === 'template') {
                            const nextKey = isTemplate ? 'template' : 'resource';
                            setError(nextKey, {
                                message: errors[key][0],
                            });
                        } else {
                            setError(normalizedKey as FieldPath<CreateReportValues>, {
                                message: errors[key][0],
                            });
                        }
                    }
                });
            } else {
                error(e.response?.data?.message ?? `Unable to create report`);
            }
            resetFormButton();
        },
    });

    return (
        <Form.Provider {...methods}>
            <form
                className={'contents'}
                onSubmit={handleSubmit(submit as SubmitHandler<CreateReportValues>)}
            >
                <Dialog.Close />
                <div className={'flex flex-col gap-6'}>
                    <Avatar.Root>
                        <Avatar.Fallback>
                            <FontAwesomeIcon icon={faChartMixed} />
                        </Avatar.Fallback>
                    </Avatar.Root>
                    <div className={'flex flex-col gap-4'}>
                        <p className={'text-xl font-medium'}>Create a report</p>
                        <Form.Label name={path('fromTemplate')} className={'text-sm font-normal'}>
                            Create a new report from scratch or customize a pre-built template.
                        </Form.Label>
                    </div>
                    <Form.UseFormRadioGroup
                        control={control}
                        name={'fromTemplate'}
                        variant={'card'}
                        className={'flex flex-col sm:flex-col sm:gap-y-2'}
                    >
                        <RadioGroup.Item className={'flex items-center gap-2'} value={'template'}>
                            <RadioGroup.IndicatorContainer className="mt-0">
                                <RadioGroup.Indicator />
                            </RadioGroup.IndicatorContainer>
                            <Label>Create from template</Label>
                        </RadioGroup.Item>
                        <RadioGroup.Item className={'flex items-center gap-2'} value={'resource'}>
                            <RadioGroup.IndicatorContainer className="mt-0">
                                <RadioGroup.Indicator />
                            </RadioGroup.IndicatorContainer>
                            <Label>Start from scratch</Label>
                        </RadioGroup.Item>
                    </Form.UseFormRadioGroup>
                    {isTemplate && (
                        <Form.Container>
                            <Form.Label name={path('template')}>Report template</Form.Label>
                            <Form.SingleSelect control={control} name="template">
                                <SingleSelect.Trigger aria-label="Choose template" />
                                <SingleSelect.Content>
                                    {templates.map(t => (
                                        <SingleSelect.Item value={t.id.toString()} key={t.id}>
                                            <SingleSelect.ItemText>{t.name}</SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                    ))}
                                </SingleSelect.Content>
                            </Form.SingleSelect>
                            <p className={'text-sm text-gray-600'}>
                                Select a template for your report.
                            </p>
                            <Form.UseFormError name={path('template')} />
                        </Form.Container>
                    )}
                    {!isTemplate && (
                        <Form.Container>
                            <Form.Label name={path('resource')}>Category</Form.Label>
                            <Form.SingleSelect control={control} name="resource">
                                <SingleSelect.Trigger aria-label="Choose category" />
                                <SingleSelect.Content>
                                    <SingleSelect.Item
                                        value="contacts"
                                        className="w-[--radix-select-trigger-width]"
                                    >
                                        <div className="flex flex-col text-base">
                                            <span className="text-gray-900">
                                                <SingleSelect.ItemText>
                                                    Contacts
                                                </SingleSelect.ItemText>
                                            </span>
                                            <span className="font-normal text-gray-600">
                                                Create a contact-based report for all of your
                                                contacts.
                                            </span>
                                        </div>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item
                                        value="transactions"
                                        className="w-[--radix-select-trigger-width]"
                                    >
                                        <div className="flex flex-col text-base">
                                            <span className="text-gray-900">
                                                <SingleSelect.ItemText>
                                                    Transactions
                                                </SingleSelect.ItemText>
                                            </span>
                                            <span className="font-normal text-gray-600">
                                                Create a transaction-based report for all of your
                                                transactions, including auction bids, donations, and
                                                tickets.
                                            </span>
                                        </div>
                                    </SingleSelect.Item>
                                    {campaignReporting && (
                                        <SingleSelect.Item
                                            value="campaigns"
                                            className="w-[--radix-select-trigger-width]"
                                        >
                                            <div className="flex flex-col text-base">
                                                <span className="text-gray-900">
                                                    <SingleSelect.ItemText>
                                                        Campaigns
                                                    </SingleSelect.ItemText>
                                                </span>
                                                <span className="font-normal text-gray-600">
                                                    Create a campaign-based report for all of your
                                                    campaigns.
                                                </span>
                                            </div>
                                        </SingleSelect.Item>
                                    )}
                                    {ticketReporting && (
                                        <SingleSelect.Item
                                            value="tickets"
                                            className="w-[--radix-select-trigger-width]"
                                        >
                                            <div className="flex flex-col text-base">
                                                <span className="text-gray-900">
                                                    <SingleSelect.ItemText>
                                                        Tickets
                                                    </SingleSelect.ItemText>
                                                </span>
                                                <span className="font-normal text-gray-600">
                                                    Create a ticket-based report for all of your
                                                    tickets.
                                                </span>
                                            </div>
                                        </SingleSelect.Item>
                                    )}
                                    {recurringPlanReporting && (
                                        <SingleSelect.Item
                                            value="transaction_plans"
                                            className="w-[--radix-select-trigger-width]"
                                        >
                                            <div className="flex flex-col text-base">
                                                <span className="text-gray-900">
                                                    <SingleSelect.ItemText>
                                                        Recurring plans
                                                    </SingleSelect.ItemText>
                                                </span>
                                                <span className="font-normal text-gray-600">
                                                    Create a recurring plan based report for all of
                                                    your recurring plans.
                                                </span>
                                            </div>
                                        </SingleSelect.Item>
                                    )}
                                    {pledgeReporting && (
                                        <SingleSelect.Item
                                            value="transaction_plans"
                                            className="w-[--radix-select-trigger-width]"
                                        >
                                            <div className="flex flex-col text-base">
                                                <span className="text-gray-900">
                                                    <SingleSelect.ItemText>
                                                        Pledges
                                                    </SingleSelect.ItemText>
                                                </span>
                                                <span className="font-normal text-gray-600">
                                                    Create a pledge-based report for all of your
                                                    pledges.
                                                </span>
                                            </div>
                                        </SingleSelect.Item>
                                    )}
                                </SingleSelect.Content>
                            </Form.SingleSelect>

                            <p className={'text-sm text-gray-600'}>
                                Select the type of report you want to build.
                            </p>
                            <Form.UseFormError name={path('resource')} />
                        </Form.Container>
                    )}
                    <Form.Container>
                        <Form.Label name={path('name')}>Report name</Form.Label>
                        <Form.TextInputRoot
                            control={control}
                            name={'name'}
                            rules={{ required: 'Please enter a name for the report' }}
                        >
                            <Form.TextInputForm />
                        </Form.TextInputRoot>
                        <Form.UseFormError name={path('name')} />
                    </Form.Container>
                    <div className={'mt-4 flex w-full flex-wrap-reverse items-center gap-3'}>
                        <div className={'flex-1'}>
                            <Dialog.Cancel asChild={true}>
                                <Button className={'w-full'} variant={'outline'} color={'neutral'}>
                                    Cancel
                                </Button>
                            </Dialog.Cancel>
                        </div>
                        <div className={'flex-1'}>
                            <Form.SubmitButton className={'w-full'} isSubmitting={isPending}>
                                Create
                            </Form.SubmitButton>
                        </div>
                    </div>
                </div>
            </form>
        </Form.Provider>
    );
};

const transformServerKeyIntoFormKey = (
    serverKey: keyof Pick<Report, 'name' | 'template_id'>,
): keyof CreateReportValues | undefined => {
    switch (serverKey) {
        case 'name':
            return 'name';
        case 'template_id':
            return 'template';
        default:
            return undefined;
    }
};

export { CreateReportDialog };
